import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import React from 'react';
import { t } from '../../../../i18n';
import { GET_INT_ENUM_VALUES, GetIntEnumValuesResponse } from '../../../../graphql/queries/getEnumValues';

/**
 * 状态国际化Hook
 * 提供状态文本、颜色等统一的国际化处理
 */
export const useStatusInternationalization = () => {
    const { i18n } = useTranslation();

    // 查询异常单状态枚举
    const { data: exceptionStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'ExceptionOrderStatus' }
    });
    const exceptionStatusMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        (exceptionStatusEnumData?.getIntEnumValues || []).forEach(item => {
            map[item.value] = item.name;
        });
        return map;
    }, [exceptionStatusEnumData]);

    /**
     * 获取状态文本的国际化版本
     * @param status 状态值（字符串或数字）
     * @returns 国际化后的状态文本
     */
    const getStatusText = (status: string | number | undefined): string => {
        if (!status) return '-';
        switch (status) {
            case 'CREATED': return t('enum.overallStatus.CREATED');
            case 'CANCELLED': return t('enum.overallStatus.CANCELLED');
            case 'ARRIVED_ORIGIN_HUB': return t('enum.overallStatus.ARRIVED_ORIGIN_HUB');
            case 'PENDING_SHIPMENT': return t('enum.overallStatus.PENDING_SHIPMENT');
            case 'LINEHAUL_PENDING_SHIPMENT': return t('enum.overallStatus.LINEHAUL_PENDING_SHIPMENT');
            case 'FEEDER_PENDING_SHIPMENT': return t('enum.overallStatus.FEEDER_PENDING_SHIPMENT');
            case 'HANDED_OVER': return t('enum.overallStatus.HANDED_OVER');
            case 'IN_TRANSIT': return t('enum.overallStatus.IN_TRANSIT');
            case 'ARRIVED_DESTINATION_HUB': return t('enum.overallStatus.ARRIVED_DESTINATION_HUB');
            case 'ARRIVED_WHS': return t('enum.overallStatus.ARRIVED_WHS');
            case 'WAITING_FOR_DELIVERY': return t('enum.overallStatus.WAITING_FOR_DELIVERY');
            case 'DELIVERY_IN_PROGRESS': return t('enum.overallStatus.DELIVERY_IN_PROGRESS');
            case 'PRE_DELIVERED': return t('enum.overallStatus.PRE_DELIVERED');
            case 'DELIVERED': return t('enum.overallStatus.DELIVERED');
            case 'DELIVERY_FAILED': return t('enum.overallStatus.DELIVERY_FAILED');
            case 'PENDING_INTERCEPTION': return t('enum.overallStatus.PENDING_INTERCEPTION');
            case 'INTERCEPTED': return t('enum.overallStatus.INTERCEPTED');
            case 'RETURN_STARTED': return t('enum.overallStatus.RETURN_STARTED');
            case 'RETURNED': return t('enum.overallStatus.RETURNED');
            case 'HOLDING': return t('enum.overallStatus.HOLDING');
            case 'RETURN_ARRIVED_DESTINATION_HUB': return t('enum.overallStatus.RETURN_ARRIVED_DESTINATION_HUB');
            case 'PICKUP_FAILED': return t('enum.overallStatus.PICKUP_FAILED');
            case 'DESTROYED': return t('enum.overallStatus.DESTROYED');
            case 'MISSING': return t('enum.overallStatus.MISSING');
            case 'DAMAGED': return t('enum.overallStatus.DAMAGED');
            default: return String(status);
        }
    };

    /**
     * 获取状态对应的背景颜色和文字颜色
     * @param status 状态值
     * @returns CSS类名字符串
     */
    const getStatusColor = (status: string | number | undefined): string => {
        if (!status) return 'bg-[#f5f5f5] text-[#8c8c8c]';
        
        // 只有派送成功显示绿色，其他都是灰色
        return status === 'DELIVERED' 
            ? 'bg-[#d9f7be] text-[#52c41a]' 
            : 'bg-[#f5f5f5] text-[#8c8c8c]';
    };

    /**
     * 获取状态渲染的完整配置
     * @param status 状态值
     * @returns 包含文本和颜色的配置对象
     */
    const getStatusConfig = (status: string | number | undefined) => {
        return {
            text: getStatusText(status),
            color: getStatusColor(status)
        };
    };

    /**
     * 获取是否投诉的国际化文本
     * @param value 布尔值
     * @returns 国际化后的文本
     */
    const getComplaintText = (value: boolean | undefined): string => {
        if (value === true) return t('config.isComplaint.yes');
        if (value === false) return t('config.isComplaint.no');
        return '-';
    };

    /**
     * 获取是否有异常的国际化文本
     * @param value 布尔值
     * @returns 国际化后的文本
     */
    const getExceptionText = (value: boolean | undefined): string => {
        if (value === true) return t('config.hasException.yes');
        if (value === false) return t('config.hasException.no');
        return '-';
    };

    /**
     * 获取异常状态的国际化文本（支持三态枚举，兜底布尔值）
     * @param value isAbnormal 布尔值
     * @param exceptionStatus 异常单三态状态（10=OPEN, 50=PROCESSED, 90=RESOLVED）
     * @returns 国际化后的文本
     */
    const getExceptionStatusText = (value: boolean | undefined, exceptionStatus?: number | null): string => {
        if (exceptionStatus != null) {
            const name = exceptionStatusMap[exceptionStatus];
            if (name) return t(`central.exceptions.exceptionStatus.${name}`);
        }
        if (value === true) return t('config.exceptionStatus.open');
        if (value === false) return t('config.exceptionStatus.resolved');
        return '-';
    };

    /**
     * 获取异常原因的国际化文本（一级）
     * @param exception 异常对象
     * @param exceptionReasonCode 异常原因代码
     * @returns 国际化后的异常原因文本
     */
    const getExceptionReasonText = (exception: any, exceptionReasonCode?: string): string => {
        if (!exception) return exceptionReasonCode || '-';
        const currentLang = i18n.language || 'en';
             
        // 标准化语言代码，处理 zh-CN, zh-TW, es-ES, es-MX 等变体
        const normalizedLang = currentLang.startsWith('zh') ? 'zh' : 
                              currentLang.startsWith('es') ? 'es' : currentLang;
        
        if (normalizedLang === 'zh' && exception.secondaryTypeZh) {
            return `${exception.secondaryTypeZh} (${exception.exceptionReasonCode})`;
        } else if (normalizedLang === 'es' && exception.secondaryTypeEs) {
            return `${exception.secondaryTypeEs} (${exception.exceptionReasonCode})`;
        } else if (exception.secondaryTypeEn) {
            return `${exception.secondaryTypeEn} (${exception.exceptionReasonCode})`;
        }
        return `${exception.exceptionReasonCode}`;
    };

    /**
     * 根据二级原因 code 从 exception.exceptionSubReasons 中取当前语言的展示文案
     * @param exception 异常字典（含 exceptionSubReasons）
     * @param subReasonCode 二级原因 code
     * @returns 展示文案，未找到则返回 code
     */
    const getExceptionSubReasonTextByCode = (exception: any, subReasonCode: string): string => {
        if (!exception?.exceptionSubReasons?.length) return subReasonCode;
        const sub = exception.exceptionSubReasons.find((s: any) => s.code === subReasonCode);
        if (!sub) return subReasonCode;
        const normalizedLang = (i18n.language || 'en').startsWith('zh') ? 'zh' :
                              (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
        if (normalizedLang === 'zh' && sub.textZh) return sub.textZh;
        if (normalizedLang === 'es' && sub.textEs) return sub.textEs;
        return sub.textEn || subReasonCode;
    };

    /**
     * 获取异常原因完整展示：有一级时显示一级；有二级原因 codes 时在一级后追加二级文案
     * @param exception 异常字典（含 exceptionSubReasons）
     * @param orderSubReasonCodes 订单上的二级原因 code 列表（可选）
     * @returns 一级文案 或 "一级 / 二级" 形式
     */
    const getExceptionReasonDisplayText = (exception: any, orderSubReasonCodes?: string[] | null): string => {
        const primary = getExceptionReasonText(exception);
        if (!orderSubReasonCodes?.length || !exception?.exceptionSubReasons?.length) {
            return primary;
        }
        const secondaryTexts = orderSubReasonCodes
            .map((code) => getExceptionSubReasonTextByCode(exception, code))
            .filter(Boolean);
        if (secondaryTexts.length === 0) return primary;
        return `${primary} - ${secondaryTexts.join('、')}`;
    };

    /**
     * 根据后端返回的英文名称获取下一步动作的国际化文本
     * @param backendName 后端返回的英文名称
     * @returns 国际化后的动作文本
     */
    const getNextActionTextFromBackend = (backendName: string): string => {
        switch (backendName) {
            case 'None': return t('config.nextAction.none');
            case 'Hold': return t('config.nextAction.hold');
            case 'ReDeliver': return t('config.nextAction.reDeliver');
            case 'Return': return t('config.nextAction.return');
            case 'Compensate': return t('config.nextAction.compensate');
            case 'ReSort': return t('config.nextAction.reSort');
            default: return backendName;
        }
    };

    /**
     * 获取揽收预约状态的国际化文本
     * @param status 状态值
     * @returns 国际化后的状态文本
     */
    const getPickupAppointmentStatusText = (status: string | undefined): string => {
        if (!status) return '-';
        
        // 严格判断每个状态值
        switch (status) {
            case 'CREATED':
                return t('enum.pickupAppointmentStatus.created');
            case 'DISPATCHING':
                return t('enum.pickupAppointmentStatus.dispatching');
            case 'DISPATCHED':
                return t('enum.pickupAppointmentStatus.dispatched');
            case 'PICKING_UP':
                return t('enum.pickupAppointmentStatus.pickingUp');
            case 'PICKED_UP':
                return t('enum.pickupAppointmentStatus.pickedUp');
            case 'PICKUP_FAILED':
                return t('enum.pickupAppointmentStatus.pickupFailed');
            case 'CANCELLED':
                return t('enum.pickupAppointmentStatus.cancelled');
            default:
                // 如果遇到未知状态，返回原值用于调试
                console.warn(`Unknown pickup appointment status: ${status}`);
                return status;
        }
    };

    /**
     * 获取揽收预约来源的国际化文本
     * @param source 来源值 (STANDING_PICKUP/MANUAL/SYSTEM_SLA/SYSTEM/COPY)
     * @returns 国际化后的来源文本
     */
    const getPickupAppointmentSourceText = (source: string | undefined): string => {
        if (!source) return '-';
        
        // 严格判断每个来源值
        switch (source) {
            case 'STANDING_PICKUP':
                return t('enum.pickupAppointmentSource.standingPickup');
            case 'MANUAL':
                return t('enum.pickupAppointmentSource.manual');
            case 'SYSTEM_SLA':
                return t('enum.pickupAppointmentSource.systemSla');
            case 'SYSTEM':
                return t('enum.pickupAppointmentSource.system');
            case 'COPY':
                return t('enum.pickupAppointmentSource.copy');
            case 'PICKUP_BOT':
                return t('enum.pickupAppointmentSource.pickupBot');
            default:
                // 如果遇到未知来源，返回原值用于调试
                console.warn(`Unknown pickup appointment source: ${source}`);
                return source;
        }
    };
    

    // 异常状态筛选选项（从后端枚举动态生成）
    const exceptionOrderStatusOptions = React.useMemo(() => {
        const enumValues = exceptionStatusEnumData?.getIntEnumValues || [];
        return enumValues.map(item => ({
            value: String(item.value),
            label: t(`central.exceptions.exceptionStatus.${item.name}`)
        }));
    }, [exceptionStatusEnumData]);

    return {
        getStatusText,
        getStatusColor,
        getStatusConfig,
        getComplaintText,
        getExceptionText,
        getExceptionStatusText,
        getExceptionReasonText,
        getExceptionSubReasonTextByCode,
        getExceptionReasonDisplayText,
        getNextActionTextFromBackend,
        getPickupAppointmentStatusText,
        getPickupAppointmentSourceText,
        exceptionStatusMap,
        exceptionOrderStatusOptions
    };
};